import Box from '@amzn/meridian/box';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import TransactionMonitoringForm from './TransactionMonitoringForm/TransactionMonitoringForm';
import RecipientReviewForm from './SDSRecipientForm/RecipientReviewForm';
import OffRoadDriverReviewForm from './SDSDriverForm/OffRoadDriverReviewForm';
import CAPReviewForm from './CAPForm/CAPReviewForm';
import DartForm from './DartForm/DartForm';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import { CustomCapMapping } from '../../Util/FormUtils';
import { FormattedMessage } from 'react-intl';
import AssociateInfo from '../CoachingForm/AssociateInfo/AssociateInfo';
import styles from './TransactionMonitoringForm/TransactionMonitoring.module.css';
import { StateContext } from '../../GlobalStateStore';
import { Profile, EmptyProfile } from '../../Types';
import DataState from '../../Types/DataState';
import DataSource from '../../api';
import {
  getCurrentWeek,
  getCurrentWeekBoundriesList,
  renderUrl,
  urls,
} from '../../Util';
import Breadcrumb, { BreadcrumbGroup } from '@amzn/meridian/breadcrumb';
import InputGroup from '@amzn/meridian/input-group';
import Select, { SelectOption } from '@amzn/meridian/select';
import { defaultCLClickstream } from '../ContactListening/utils/ContactListeningHelpers';
import PlaybackModal from '../ContactReview/Util/PlaybackModal';
import moment from 'moment';
import {
  ConsolidatedContact,
  ContactStatusOptionsObject,
  RecommendedContactStatusOptionsObject,
} from '../../api/CoachingNotes/schema';
import PerformancePortalContext from '../PerformancePortalContext';
import BackButton from '../../Util/generic/BackButton';
import CCXDetailsPanel from '../CCXDetails/CCXDetailsPanel';
import { QueryVariablesContactListeningData } from '../../api/Naws';
import { useNavigate } from 'react-router';
import Loader from '@amzn/meridian/loader';
import { getVerticals } from '../CoachingForm/ContactReview/Data';
import { useIntl } from 'react-intl';
import Text from '@amzn/meridian/text';
import { QuestionOption } from '../../Util/Form/formData.types';
import { Feature, isFeatureEnabled } from '../../config/FeaturesConfig';
import Alert from '@amzn/meridian/alert';
import { isEqual } from 'lodash';
import {
  fetchContactStatus,
  handleRealtimeHMDContact,
  OUTLIER_INGRESS,
} from './utils/helper';
import { ContactStatus } from '../Dashboard/DashboardComponents/HmdDetails/types';
import { EmptyContactStatus } from '../Dashboard/DashboardComponents/HmdDetails/helper';
import { FormSaveType } from '../../Util/Form/formData.constants';
import { isFormReviewedOrDelivered } from '../../Util/Form/helpers';
import { SelectOptionValue } from '@amzn/meridian/select/select-option';
import { Vertical } from '@amzn/aet-coachingform-kit';
import {
  defaultFormOptions,
  applyBackwardCompatibilityRules,
} from './utils/constants';
import { FORM_DISPLAY_NAMES } from '../../Util/Form/formSectionConstants';
import DSLReviewForm from './SDSDSLForm/DSLReviewForm';

interface FormState {
  contactInfo?: ConsolidatedContact;
}

interface CCXDetailsInfo {
  ccxOverall: string;
  ccxKnowledgable: string;
  ccxEfficiency: string;
  ccxHelpfulness: string;
  ccxEmpathy: string;
  ccxCourtesy: string;
  ccxUnderstand: string;
}

export type Option = React.ComponentProps<typeof SelectOption>;

export type ValueType = number | string | Array<number | string>;

export const getContactReviewFormTypeOptions = (
  enabledSDSForm: boolean,
  enabledD2Form: boolean,
  enableSDSRecipientFormUpdates: boolean,
  enableSDSDSLForm: boolean,
  enableSDSOffroadDriverFormUpdates: boolean,
  enableDartForm: boolean,
  verticalKey: number
): QuestionOption[] => {
  // Legacy logic
  if (!enabledSDSForm) {
    return [
      {
        DEFAULT_MESSAGE: 'Transaction monitoring form',
        TRANSLATION_ID: 'cs_perfportal_tx_monitoring_form_title',
        VALUE: 0,
      },
    ];
  }

  // 1. Handle the base case
  let formOptions = defaultFormOptions[verticalKey] ?? [
    {
      DEFAULT_MESSAGE: 'Transaction monitoring form',
      TRANSLATION_ID: 'cs_perfportal_tx_monitoring_form_title',
      VALUE: 0,
    },
  ];

  // 2. Apply any feature flag gatted customizations
  if (enableSDSRecipientFormUpdates && verticalKey == 2) {
    formOptions = [
      {
        DEFAULT_MESSAGE: 'Recipient review form',
        TRANSLATION_ID: 'cs_perfportal_recipient_review_form_title',
        VALUE: 1,
      },
    ];
  }

  // 3. Apply D2AS feature flag customizations
  if (enabledD2Form && verticalKey == 1) {
    formOptions = [
      {
        DEFAULT_MESSAGE: 'D2AS Contact Review',
        TRANSLATION_ID: 'cs_perfportal_d2as_contact_review_form_title',
        VALUE: 4,
      },
    ];
  }

  // 4. Apply SDS DSL feature flag gatted customizations
  if (enableSDSDSLForm && verticalKey == 9) {
    formOptions = [
      {
        DEFAULT_MESSAGE: 'DSL review form',
        TRANSLATION_ID: 'cs_perfportal_dsl_review_form_title',
        VALUE: 4,
      },
    ];
  }

  // 5. Apply SDS Offroad Driver feature flag gatted customizations
  if (enableSDSOffroadDriverFormUpdates && verticalKey == 3) {
    formOptions = [
      {
        DEFAULT_MESSAGE: 'Non-Delivery review form',
        TRANSLATION_ID: 'cs_perfportal_non_delivery_review_form_title',
        VALUE: 1,
      },
    ];
  }

  // 6. Apply DART feature flag customizations
  if (enableDartForm && verticalKey == Vertical.DART) {
    formOptions = [
      {
        DEFAULT_MESSAGE: 'DART Escalations Form',
        TRANSLATION_ID: 'cs_perfportal_dart_escalations_form_title',
        VALUE: 7,
      },
      {
        DEFAULT_MESSAGE: 'DART Consultation Form',
        TRANSLATION_ID: 'cs_perfportal_dart_consultation_form_title',
        VALUE: 8,
      },
    ];
  }

  // 7. Return the formOptions after establishing base case then applying feature flags
  return formOptions;
};

const mapCCXDetailsParamsToObj = (
  str: string | undefined
): CCXDetailsInfo | undefined => {
  if (!str) {
    return void 0;
  }
  const parts = str.split(',');
  const [
    ccxOverall,
    ccxEmpathy,
    ccxCourtesy,
    ccxKnowledgable,
    ccxUnderstand,
    ccxHelpfulness,
    ccxEfficiency,
  ] = parts;
  return {
    ccxCourtesy,
    ccxEfficiency,
    ccxEmpathy,
    ccxHelpfulness,
    ccxKnowledgable,
    ccxOverall,
    ccxUnderstand,
  };
};

const FormLibraryHome: React.FC = (): React.ReactElement => {
  const enabledCCXDetails = isFeatureEnabled(
    Feature.ENABLE_HISTORICAL_CCX_DETAIL
  );
  const enabledSDSForm = isFeatureEnabled(Feature.ENABLE_SDS_FORM);
  const enableSDSRecipientFormUpdates = isFeatureEnabled(
    Feature.ENABLE_SDS_RECIPIENT_FORM_UPDATES
  );
  const enableSDSOffroadDriverFormUpdates = isFeatureEnabled(
    Feature.ENABLE_SDS_OFFROAD_DRIVER_FORM_UPDATES
  );
  const enableSDSDSLForm = isFeatureEnabled(Feature.ENABLE_SDS_DSL_FORM);
  const enabledD2Form = isFeatureEnabled(Feature.ENABLE_D2_FORM);
  const enabledDartFormFeature = isFeatureEnabled(Feature.ENABLE_DART_FORM);

  const apiNaws = DataSource.getDataFeed('NAWS');
  const intl = useIntl();

  const { userId, contactId } = useParams() as {
    userId: string;
    contactId: string;
  };
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const ccxRatings = params.get('ccxRatings');
  const pUserID = params.get('pUserID');
  const verticalValue = params.get('verticalValue');
  const skillValue = params.get('skillValue');
  const segmentValue = params.get('segmentValue');
  const channelValue = params.get('channelValue');
  const marketplaceValue = params.get('marketplaceValue');
  const weekValue = params.get('weekValue');
  const ingress = params.get('ingress');

  const date = getCurrentWeekBoundriesList();

  const [formType, setFormType] = useState('');
  const globalState = useContext(StateContext);
  const [profile, setProfile] = useState<Profile>(EmptyProfile);
  const [selectedContactID, setSelectedContactID] = useState(contactId);
  const [, setProfileState] = useState(DataState.LOADING);
  const [contactIDs, setContactIDs] = useState<string[]>([]);
  const [contactList, setContactList] = useState<ConsolidatedContact[]>([]);
  const [selectedContactInfo, setSelectedContactInfo] =
    useState<ConsolidatedContact>();
  const [contactListState, setContactListState] = useState(DataState.LOADING);
  const [contactStatusDetails, setContactStatusDetails] =
    useState<ContactStatus>(EmptyContactStatus);
  const [contactStatusDetailsState, setContactStatusDetailsState] = useState(
    DataState.LOADING
  );
  const [formState, setFormState] = useState<Partial<FormState>>({});
  const [selectedVertical, setSelectedVertical] = useState('');
  const [currentReviewOptions, setCurrentReviewOptions] = useState<
    QuestionOption[]
  >([
    {
      DEFAULT_MESSAGE: 'Transaction monitoring form',
      TRANSLATION_ID: 'cs_perfportal_tx_monitoring_form_title',
      VALUE: 0,
    },
  ]);

  const [, setContactListeningParams] =
    useState<QueryVariablesContactListeningData>();

  // TODO - remove our reliance on PerformancePortalContext in form library
  const { performancePortalContextValue, setPerformancePortalContextValue } =
    useContext(PerformancePortalContext);

  const [ccxDetailsInfo, setCCXDetailsInfo] = useState<
    CCXDetailsInfo | undefined
  >(
    mapCCXDetailsParamsToObj(
      performancePortalContextValue?.ccxRatings
    ) as CCXDetailsInfo
  );

  const [ccxForContactData, setCCXForContactData] = useState<any>(undefined);
  const [ccxForContactDataState, setCCXForContactDataState] = useState(
    DataState.LOADING
  );

  useEffect(() => {
    setCCXDetailsInfo(
      mapCCXDetailsParamsToObj(
        ccxRatings ?? performancePortalContextValue?.ccxRatings
      ) as CCXDetailsInfo
    );
  }, [ccxRatings, performancePortalContextValue?.ccxRatings]);

  // repopulate context after page refresh if URL params are present
  if (setPerformancePortalContextValue) {
    let toSet = {
      ...performancePortalContextValue,
    };
    if (ccxRatings && !performancePortalContextValue?.ccxRatings) {
      toSet = {
        ...toSet,
        ccxRatings,
      };
    }
    if (pUserID && !performancePortalContextValue?.passedCCXUserID) {
      toSet = {
        ...toSet,
        passedCCXUserID: pUserID,
      };
    }
    if (!isEqual(toSet, performancePortalContextValue)) {
      setPerformancePortalContextValue(toSet);
    }
  }

  const [pageRefreshState, setPageRefreshState] = useState(DataState.LOADING);

  const [commonFormTemplate, setCommonFormTemplate] = useState<any>(void 0);
  const [commonFormTemplateState, setCommonFormTemplateState] = useState(
    DataState.LOADING
  );

  const [
    contactNotPresentInGetConsolidated,
    setContactNotPresentInGetConsolidated,
  ] = useState(false);

  const { inspireOptions } = globalState?.state;
  const { cscEntityPrefix } = inspireOptions.data;

  const getLabelWrapper = (label: SelectOptionValue): string => {
    if (label.toString() === CustomCapMapping.SDSCAP)
      return CustomCapMapping.CAP;
    return label.toString();
  };

  const handleVerticalChange = (vertical: ValueType): void => {
    setCurrentReviewOptions(
      getContactReviewFormTypeOptions(
        enabledSDSForm,
        enabledD2Form,
        enableSDSRecipientFormUpdates,
        enableSDSDSLForm,
        enableSDSOffroadDriverFormUpdates,
        enabledDartFormFeature,
        Vertical[vertical as keyof Vertical]
      )
    );
    setSelectedVertical(vertical as string);
    setFormType('');
  };

  // GGGG refers to the year of the week. This may be different from YYYY
  // When part of the week is from 2024 and part of it is from 2025. e.g. 2024-12-30 (Monday)
  // In such cases, GGGG refers to the new year (e..g 2025) and YYYY refers to the previous year (e.g. 2024).
  const startWeek = moment().subtract(4, 'w').format('GGGG-WW');
  const endWeek = moment().format('GGGG-WW');

  const navigate = useNavigate();

  const vertical = getVerticals();

  // Get profile data for user on page load
  useEffect(() => {
    if (inspireOptions.state === DataState.LOADED && userId) {
      const queryMigrationVariables = apiNaws.prepareQuery(
        getCurrentWeek(),
        userId,
        cscEntityPrefix
      );
      apiNaws.getProfileData(
        queryMigrationVariables,
        setProfile,
        setProfileState
      );
    }
  }, [inspireOptions.state, userId]);

  useEffect(() => {
    setFormState({
      contactInfo:
        contactList?.filter(c => c.contactID === selectedContactID)?.[0] ?? {},
    });

    setSelectedContactInfo(
      contactList?.filter(c => c.contactID === selectedContactID)?.[0] ?? {}
    );
  }, [selectedContactID]);

  // on initial load use effect
  useEffect(() => {
    apiNaws.getFormTemplateData(
      { VERTICAL: 'Total' },
      setCommonFormTemplate,
      setCommonFormTemplateState
    );

    if (
      !performancePortalContextValue?.passedCCXUserID &&
      setPerformancePortalContextValue
    ) {
      let ctxChanges = {
        ...performancePortalContextValue,
      };

      if (
        weekValue &&
        skillValue &&
        verticalValue &&
        segmentValue &&
        channelValue &&
        marketplaceValue
      ) {
        ctxChanges = {
          ...performancePortalContextValue,
          dashboardFilters: {
            ...performancePortalContextValue?.dashboardFilters,
            verticalValue,
            filterVerticalValue: verticalValue,
            channelValue,
            marketplaceValue,
            weekValue,
            segmentValue,
            skillValue,
          },
        };
      }

      if (pUserID) {
        ctxChanges = {
          ...ctxChanges,
          passedCCXUserID: pUserID,
        };
      }
      if (ccxRatings) {
        ctxChanges = {
          ...ctxChanges,
          ccxRatings,
        };
      }
      setPerformancePortalContextValue(ctxChanges);
    }
  }, []);

  const refreshContacts = useCallback(() => {
    const { userName } = profile.user;
    if (userName !== '') {
      const userID = userId ? `${cscEntityPrefix}:${userId}` : '';
      const queryVariablesNaws = apiNaws.prepareQueryforRecommendationsGetter(
        userID,
        userName,
        startWeek,
        endWeek
      );
      apiNaws.getConsolidatedContacts(
        queryVariablesNaws,
        setContactList,
        setContactListState
      );
    }
  }, [profile]);

  useEffect(() => {
    refreshContacts();
  }, [profile]);

  useEffect(() => {
    fetchContactStatus(
      ingress,
      profile,
      cscEntityPrefix,
      date,
      apiNaws,
      setContactStatusDetails,
      setContactStatusDetailsState
    );
  }, [ingress, profile]);

  useEffect(() => {
    handleRealtimeHMDContact(
      contactId,
      ingress,
      contactList,
      contactListState,
      contactStatusDetailsState,
      profile,
      cscEntityPrefix,
      contactStatusDetails,
      setContactList,
      setContactListState
    );
  }, [
    contactId,
    ingress,
    contactList,
    profile,
    contactListState,
    contactStatusDetailsState,
  ]);

  // if ccxDetailsInfo isn't passed through (url params), it means we came from coaching
  // in this case, we need to call different api to get the data to load ccxDetails
  // also, only call if it doesn't exist in context already

  // case 1: page loaded with contact ID. This means they cannot select other contactIDs
  // only call api IF populated in context
  useEffect(() => {
    if (
      formState?.contactInfo?.userID &&
      contactId &&
      !ccxDetailsInfo &&
      !performancePortalContextValue?.ccxForContactData?.[contactId]
    ) {
      const params = {
        userID: formState?.contactInfo?.userID,
        startDate: startWeek,
        endDate: endWeek,
        contactID: contactId,
      };
      apiNaws.getCCXForContactData(
        params,
        setCCXForContactData,
        setCCXForContactDataState
      );
    }
  }, [formState?.contactInfo?.userID, ccxDetailsInfo, contactId]);

  // case 2: contactId is not defined, they can select a contact ID.
  // in this case, we need to watch for those changes and recall API/repopulate context

  useEffect(() => {
    if (
      !contactId &&
      selectedContactInfo?.userID &&
      selectedContactID &&
      !performancePortalContextValue?.ccxForContactData?.[selectedContactID]
    ) {
      const params = {
        userID: selectedContactInfo?.userID,
        startDate: startWeek,
        endDate: endWeek,
        contactID: selectedContactID,
      };
      apiNaws.getCCXForContactData(
        params,
        setCCXForContactData,
        setCCXForContactDataState
      );
    }
  }, [contactId, selectedContactInfo?.userID, selectedContactID]);

  // once we receive the data from api, we need to update the context
  useEffect(() => {
    if (
      ccxForContactDataState === DataState.LOADED &&
      setPerformancePortalContextValue
    ) {
      setPerformancePortalContextValue({
        ...performancePortalContextValue,
        ccxForContactData: {
          ...performancePortalContextValue?.ccxForContactData,
          // create the data mapping for this contactID
          [contactId ?? selectedContactID]: ccxForContactData.details,
        },
      });
    }
  }, [ccxForContactData, ccxForContactDataState]);

  // once the context is updated with the new data, we need to update the ccxDetailsInfo
  // this will populate the ccx component on the page
  useEffect(() => {
    const ccxDetailsItem =
      performancePortalContextValue?.ccxForContactData?.[
        contactId ?? selectedContactID
      ]?.[0];
    // if the first element exists, populate with it
    // api should only ever return 1 item
    if (ccxDetailsItem) {
      const newCcxDetailsInfo = {
        ccxOverall: ccxDetailsItem.ccxOverall,
        ccxKnowledgable: ccxDetailsItem.ccxKnowledgeable,
        ccxEfficiency: ccxDetailsItem.ccxEfficiency,
        ccxHelpfulness: ccxDetailsItem.ccxHelpfulness,
        ccxEmpathy: ccxDetailsItem.ccxEmpathy,
        ccxCourtesy: ccxDetailsItem.ccxCourtesy,
        ccxUnderstand: ccxDetailsItem.ccxUnderstand,
      } as CCXDetailsInfo;
      setCCXDetailsInfo(newCcxDetailsInfo);
    } else {
      setCCXDetailsInfo(undefined);
    }
  }, [performancePortalContextValue?.ccxForContactData, selectedContactID]);

  useEffect(() => {
    if (contactList?.length > 0) {
      if (contactId) {
        const contactInfo = contactList?.find(c => c.contactID === contactId);
        setFormState(prevFormState => ({ ...prevFormState, contactInfo }));
      }
      setContactIDs(
        contactList
          .filter(
            contact =>
              contact.status ===
              RecommendedContactStatusOptionsObject.NOTREVIEWED
          )
          .map(c => c.contactID)
      );
    }
  }, [contactList]);

  // This use effect is to assist rendering data for draft forms
  // The Vertical and Form Type tab are rendered at FormLibraryLevel
  // Then passed into each individual form, so we update these here
  useEffect(() => {
    if (contactListState === DataState.LOADED) {
      if (contactId) {
        const contactDetails = contactList.find(c => c.contactID === contactId);

        if (contactDetails?.formReviews) {
          const existingVertical =
            contactDetails.formReviews[0].formAnswer.find(answer =>
              answer.questionID.includes('VERTICAL')
            );
          const existingFormType =
            contactDetails.formReviews[0].formAnswer.find(answer =>
              answer.questionID.includes('CONTACT_REVIEW_FORM_TYPE')
            );
          let verticalBeMapped = '';
          if (
            existingVertical &&
            existingVertical.selectedOption &&
            existingFormType &&
            existingFormType.selectedOption
          ) {
            verticalBeMapped = existingVertical.selectedOption.defaultMessage;
            // Backward compatible with previous form type be Transaction Monitoring Form
            // This is to normalize the way how we persist for form selection
            const lowerCaseFormType =
              existingFormType.selectedOption.defaultMessage.toLowerCase();

            let normalizedFormType;
            if (lowerCaseFormType === 'cap review form') {
              normalizedFormType = 'CAP Review Form';
            } else if (lowerCaseFormType === 'd2as contact review') {
              normalizedFormType = 'D2AS Contact Review';
            } else if (lowerCaseFormType === 'dsl review form') {
              normalizedFormType = 'DSL review form';
            } else if (lowerCaseFormType.includes('escalations')) {
              normalizedFormType = FORM_DISPLAY_NAMES.DART_ESCALATIONS_FORM;
            } else if (lowerCaseFormType.includes('consultation')) {
              normalizedFormType = FORM_DISPLAY_NAMES.DART_CONSULTATION_FORM;
            } else {
              normalizedFormType =
                lowerCaseFormType[0].toUpperCase() + lowerCaseFormType.slice(1);
            }

            setSelectedVertical(verticalBeMapped);

            // Get standard options based on current feature flags
            const baseOptions = getContactReviewFormTypeOptions(
              enabledSDSForm,
              enabledD2Form,
              enableSDSRecipientFormUpdates,
              enableSDSDSLForm,
              enableSDSOffroadDriverFormUpdates,
              enabledDartFormFeature,
              Vertical[verticalBeMapped as keyof Vertical]
            );

            // Apply backward compatibility rules for draft forms
            const options = applyBackwardCompatibilityRules(
              baseOptions,
              verticalBeMapped,
              normalizedFormType,
              enabledD2Form
            );

            setCurrentReviewOptions(options);
            setFormType(normalizedFormType);
          } else {
            verticalBeMapped = '';
            setSelectedVertical(verticalBeMapped);
          }
          setPageRefreshState(DataState.LOADED);
        } else {
          setPageRefreshState(DataState.LOADED);
        }
      } else {
        setPageRefreshState(DataState.LOADED);
      }
    }
  }, [contactList, contactId, contactListState]);

  useEffect(() => {
    setCCXDetailsInfo(
      mapCCXDetailsParamsToObj(
        performancePortalContextValue?.ccxRatings
      ) as CCXDetailsInfo
    );
  }, [performancePortalContextValue?.ccxRatings]);

  useEffect(() => {
    if (
      contactListState === DataState.LOADED &&
      contactList &&
      (contactId || selectedContactID)
    ) {
      if (contactId) {
        setContactNotPresentInGetConsolidated(
          !contactList.some(contact => contact.contactID === contactId)
        );
      } else if (selectedContactID) {
        setContactNotPresentInGetConsolidated(
          !contactList.some(contact => contact.contactID === selectedContactID)
        );
      }
    }
    if (contactListState === DataState.ERROR) {
      setContactNotPresentInGetConsolidated(true);
    }
  }, [contactId, contactList, selectedContactID, contactListState]);

  const passedUserID =
    performancePortalContextValue?.passedCCXUserID ?? pUserID;

  const backURL =
    ingress === OUTLIER_INGRESS
      ? urls.outlierManagement
      : ingress === 'realtimeHMD'
        ? urls.realTimeHmdDetails +
          (passedUserID ? `/${passedUserID}` : `/${userId}`)
        : urls.ccxDetails + (passedUserID ? `/${passedUserID}` : `/${userId}`);

  const paramsExist = backURL.includes('?');
  const redirectURLParams = `${
    paramsExist ? '&' : '?'
  }verticalValue=${verticalValue}&skillValue=${skillValue}&segmentValue=${segmentValue}&channelValue=${channelValue}&marketplaceValue=${marketplaceValue}&weekValue=${weekValue}`;

  const ccxParams = 'rating=1,2,3,4,5';

  const breadcrumbs =
    ingress === OUTLIER_INGRESS ? (
      <BackButton backURL={backURL} />
    ) : passedUserID ? (
      <div className={styles.ccxDetailsBreadcrumbsContainer}>
        <BreadcrumbGroup>
          <Breadcrumb
            onClick={() =>
              navigate(
                `${renderUrl(
                  `${urls.dashboard}/${passedUserID}${
                    ingress === 'realtimeHMD' ? '' : redirectURLParams
                  }`
                )}`
              )
            }
          >
            <FormattedMessage
              id='cs_perfportal_dashboard'
              defaultMessage='Dashboard'
              description='Dashboard button'
            />
          </Breadcrumb>
          <Breadcrumb
            onClick={() =>
              navigate(
                `${renderUrl(backURL)}${
                  ingress === 'realtimeHMD' ? '' : redirectURLParams
                }`
              )
            }
          >
            <FormattedMessage
              id={
                ingress === 'realtimeHMD'
                  ? 'cs_perfportal_real_time_hmd'
                  : 'cs_perfportal_ccx_details'
              }
              defaultMessage={
                ingress === 'realtimeHMD' ? 'Real-time HMD' : 'CCX details'
              }
            />
          </Breadcrumb>
          <Breadcrumb>
            <FormattedMessage
              id='cs_perfportal_contact_review'
              defaultMessage='Contact review'
            />
          </Breadcrumb>
        </BreadcrumbGroup>
        <BackButton
          backURL={
            ingress === 'realtimeHMD'
              ? backURL
              : `${backURL}?${ccxParams}${redirectURLParams}`
          }
        />
      </div>
    ) : (
      <BreadcrumbGroup>
        <Breadcrumb href={renderUrl(urls.team)} onClick={navigate}>
          <FormattedMessage id='cs_perfportal_team' defaultMessage='Team' />
        </Breadcrumb>
        <Breadcrumb
          href={renderUrl(`${urls.dashboard}/${userId}`)}
          onClick={navigate}
        >
          {profile.user.displayName}
        </Breadcrumb>
        <Breadcrumb
          href={renderUrl(`${urls.coachingNotes}/${userId}`)}
          onClick={navigate}
          data-cy='contact-review-form-coaching-notes-breadcrumb'
        >
          <FormattedMessage
            id='cs_perfportal_coaching_notes'
            defaultMessage='Coaching notes'
          />
        </Breadcrumb>
        <Breadcrumb>
          <FormattedMessage
            id='cs_perfportal_contact_review'
            defaultMessage='Contact review'
          />
        </Breadcrumb>
      </BreadcrumbGroup>
    );

  const contactInformation = (
    <Box
      type='outline'
      spacingInset='500'
      height='314px'
      className={styles.contactInformation}
    >
      <Row className={styles.contactinfo}>
        <Heading level={3} type='h300' className={styles.headingMargin}>
          <FormattedMessage
            id='cs_perfportal_coaching_contact_information'
            description='Contact information panel heading'
            defaultMessage='Contact information'
          />
        </Heading>
        <div className={styles.contactInformationInputGroup}>
          <InputGroup size='xlarge' direction='column' disabled={!!contactId}>
            <Select
              value={selectedContactID}
              onChange={setSelectedContactID}
              placeholder={selectedContactID}
            >
              {contactIDs.map(contactID => (
                <SelectOption
                  key={contactID}
                  value={contactID}
                  label={contactID}
                />
              ))}
            </Select>
          </InputGroup>
          {selectedContactID && (
            <div className={styles.playbackModalContainer}>
              <PlaybackModal
                parentValue={contactList?.find(
                  contact => contact.contactID === selectedContactID
                )}
                setContactListeningParams={setContactListeningParams}
                CLClickstream={defaultCLClickstream}
              />
            </div>
          )}
        </div>
      </Row>
      <Row className={styles.unorderedListBlockContactInformation}>
        <div className={styles.unorderedListContainer}>
          <ul className={styles.unorderedList}>
            {formState?.contactInfo?.contactDate && (
              <li>
                <FormattedMessage
                  id='cs_perfportal_contact_recom_contact_date'
                  defaultMessage='Contact date'
                />
                :{' '}
                {new Date(
                  formState?.contactInfo?.contactDate
                ).toLocaleDateString()}
              </li>
            )}
            {formState?.contactInfo?.sic && (
              <li>
                <FormattedMessage
                  id='cs_perfportal_contact_recom_sic_code'
                  defaultMessage='SIC code'
                  description='SIC code for recommended contact'
                />
                : {formState?.contactInfo?.sic}
              </li>
            )}
            {formState?.contactInfo?.duration && (
              <li>
                <FormattedMessage
                  id='cs_perfportal_duration'
                  defaultMessage='Duration'
                />
                : {formState?.contactInfo?.duration}
              </li>
            )}
            {formState?.contactInfo?.channel && (
              <li>
                <FormattedMessage
                  id='cs_perfportal_channel'
                  defaultMessage='Channel'
                />
                : {formState?.contactInfo?.channel}
              </li>
            )}
            {formState?.contactInfo?.category && (
              <li>
                <FormattedMessage
                  id='cs_perfportal_category'
                  defaultMessage='Category'
                />
                : {formState?.contactInfo?.category}
              </li>
            )}
            {formState?.contactInfo?.marketplaceDomainName && (
              <li>
                <FormattedMessage
                  id='cs_perfportal_marketplace'
                  defaultMessage='Marketplace'
                />
                : {formState?.contactInfo?.marketplaceDomainName}
              </li>
            )}
          </ul>
        </div>
      </Row>
    </Box>
  );

  const contactNotPrenetInGetConsolidatedToaster = (
    <Box spacingInset='400'>
      <Alert type='neutral' toast={true}>
        <Text alignment='center'>
          <FormattedMessage
            id='cs_perfportal_tx_monitoring_old_contact'
            defaultMessage='Selected contact is too recent (< 48h) or too old (> 4 weeks). Unable to review.'
          />
        </Text>
      </Alert>
    </Box>
  );

  if (
    contactListState === DataState.LOADING ||
    pageRefreshState === DataState.LOADING ||
    commonFormTemplateState === DataState.LOADING
  ) {
    return <Loader />;
  }

  const isReviewedOrDelivered = isFormReviewedOrDelivered(
    selectedContactID,
    formState.contactInfo?.contactStatus
  );

  return (
    <Box>
      <Row spacingInset='300 none 500 none'>{breadcrumbs}</Row>
      <Row>
        <Heading level={1} type='h400'>
          <FormattedMessage
            id='cs_perfportal_contact_review'
            defaultMessage='Contact review'
          />
        </Heading>
      </Row>
      <Row>
        <p className={styles.forwardCultureHeading}>
          <FormattedMessage
            id='cs_perfportal_coaching_is_an_opportunity'
            defaultMessage='Coaching is an opportunity to build trust and demonstrate support for your associates.'
          />
        </p>
      </Row>
      <Row>
        <p className={styles.forwardCultureSubText}>
          <FormattedMessage
            id='cs_perfportal_tx_monitoring_autosave_message'
            defaultMessage='This form is auto-saved. Asterisks (*) indicate required fields'
          />
        </p>
      </Row>
      <Row>
        <AssociateInfo
          initialValues={undefined}
          profile={
            performancePortalContextValue?.passedProfile
              ? ({
                  user: performancePortalContextValue?.passedProfile,
                } as unknown as Profile)
              : profile
          }
        />
      </Row>
      <Row>
        {contactInformation}
        {ccxDetailsInfo && enabledCCXDetails && (
          <div className={styles.contactInformation}>
            <CCXDetailsPanel
              overall={+ccxDetailsInfo.ccxOverall}
              knowledgeable={+ccxDetailsInfo.ccxKnowledgable}
              efficiency={+ccxDetailsInfo.ccxEfficiency}
              helpfulness={+ccxDetailsInfo.ccxHelpfulness}
              empathy={+ccxDetailsInfo.ccxEmpathy}
              courtesy={+ccxDetailsInfo.ccxCourtesy}
              understandability={+ccxDetailsInfo.ccxUnderstand}
            />
          </div>
        )}
      </Row>
      {contactNotPresentInGetConsolidated ? (
        <Row>{contactNotPrenetInGetConsolidatedToaster}</Row>
      ) : (
        contactListState === DataState.LOADED && (
          <>
            <Row className={styles.title}>
              <Text type='h400'>
                <FormattedMessage
                  id='cs_perfportal_review_form_select_form'
                  defaultMessage='Select form'
                />
              </Text>
            </Row>
            <Row className={styles.title}>
              <InputGroup size='xlarge' direction='column'>
                <Select
                  value={selectedVertical}
                  onChange={handleVerticalChange}
                  disabled={isReviewedOrDelivered}
                  data-cy='select-vertical'
                  label={intl.formatMessage({
                    id: 'cs_perfportal_contact_review_label_required_vertical',
                    defaultMessage: 'Vertical *',
                  })}
                >
                  {vertical.map((option: Option) => (
                    <SelectOption
                      value={option.value}
                      label={getLabelWrapper(option.label)}
                    />
                  ))}
                </Select>
              </InputGroup>
            </Row>
            {selectedVertical && (
              <Row className={styles.title}>
                <InputGroup size='xlarge' direction='column'>
                  <Select
                    value={formType}
                    onChange={current => setFormType(current)}
                    disabled={isReviewedOrDelivered}
                    data-cy='select-form'
                    label={intl.formatMessage({
                      id: 'cs_perfportal_tx_monitoring_contact_review_form_type',
                      defaultMessage: 'Contact review form type *',
                    })}
                  >
                    {currentReviewOptions?.map((option: QuestionOption) => (
                      <SelectOption
                        value={option.DEFAULT_MESSAGE}
                        data-cy={option.DEFAULT_MESSAGE}
                        label={intl.formatMessage({
                          id: option.TRANSLATION_ID,
                          defaultMessage: option.DEFAULT_MESSAGE,
                        })}
                        key={option.TRANSLATION_ID}
                      />
                    ))}
                  </Select>
                </InputGroup>
              </Row>
            )}
            {selectedVertical &&
            formType === FORM_DISPLAY_NAMES.TRANSACTION_MONITORING_FORM ? (
              <TransactionMonitoringForm
                userId={userId}
                contactId={contactId || selectedContactID}
                selectedVertical={selectedVertical}
                formType={formType}
                ccxDetailsInfo={ccxDetailsInfo}
                ccxRatings={ccxRatings}
                passedProfile={performancePortalContextValue?.passedProfile}
                ingress={ingress}
              />
            ) : selectedVertical &&
              formType === FORM_DISPLAY_NAMES.RECIPIENT_REVIEW_FORM ? (
              <RecipientReviewForm
                userId={userId}
                contactId={contactId || selectedContactID}
                selectedVertical={selectedVertical}
                formType={formType}
                ccxDetailsInfo={ccxDetailsInfo}
                passedProfile={performancePortalContextValue?.passedProfile}
                ingress={ingress}
              />
            ) : selectedVertical &&
              formType === FORM_DISPLAY_NAMES.NON_DELIVERY_REVIEW_FORM ? (
              <OffRoadDriverReviewForm
                userId={userId}
                contactId={contactId || selectedContactID}
                selectedVertical={selectedVertical}
                formType={formType}
                ccxDetailsInfo={ccxDetailsInfo}
                passedProfile={performancePortalContextValue?.passedProfile}
                ingress={ingress}
              />
            ) : selectedVertical && formType === 'DSL review form' ? (
              <DSLReviewForm
                userId={userId}
                contactId={contactId || selectedContactID}
                selectedVertical={selectedVertical}
                formType={formType}
                ccxDetailsInfo={ccxDetailsInfo}
                passedProfile={performancePortalContextValue?.passedProfile}
                ingress={ingress}
              />
            ) : selectedVertical &&
              formType === FORM_DISPLAY_NAMES.CAP_REVIEW_FORM ? (
              <CAPReviewForm
                userId={userId}
                contactId={contactId || selectedContactID}
                selectedVertical={selectedVertical}
                formType={formType}
                ccxDetailsInfo={ccxDetailsInfo}
                passedProfile={performancePortalContextValue?.passedProfile}
                ingress={ingress}
                passedContactInfo={formState?.contactInfo}
              />
            ) : selectedVertical &&
              (formType === FORM_DISPLAY_NAMES.DART_ESCALATIONS_FORM ||
                formType === FORM_DISPLAY_NAMES.DART_CONSULTATION_FORM) ? (
              <DartForm
                userId={userId}
                contactId={contactId || selectedContactID}
                selectedVertical={selectedVertical}
                formType={formType}
                ccxDetailsInfo={ccxDetailsInfo}
                // ccxRatings={ccxRatings}
                passedProfile={performancePortalContextValue?.passedProfile}
                ingress={ingress}
              />
            ) : (
              selectedVertical &&
              formType === FORM_DISPLAY_NAMES.D2AS_CONTACT_REVIEW && (
                <TransactionMonitoringForm
                  userId={userId}
                  contactId={contactId || selectedContactID}
                  selectedVertical={selectedVertical}
                  formType={formType}
                  ccxDetailsInfo={ccxDetailsInfo}
                  ccxRatings={ccxRatings}
                  passedProfile={performancePortalContextValue?.passedProfile}
                  ingress={ingress}
                />
              )
            )}
          </>
        )
      )}
    </Box>
  );
};
export default FormLibraryHome;
